import { UkDateInput, ukDate } from "@/components/UkDateInput";

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, Mail, Phone, Trash2, ShieldCheck, FileCheck, PoundSterling, Clock, KeyRound,
  Upload, FileText, Download, Car, SlidersHorizontal, Briefcase, Ban, HeartHandshake, MapPin, Pencil, FileSignature,
} from "lucide-react";
import { statusTone as tone, HeaderAccent } from "@/lib/status";
import { PAGES } from "@/lib/permissions";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { ActivityGallery } from "@/components/ActivityGallery";
import { WorkPrefsEditor } from "@/components/WorkPrefsEditor";
import { CoverageView } from "@/components/CoverageView";
import { StaffAgreementsAdmin, ContractsList } from "@/components/StaffAgreements";
import { PayRatesEditor } from "@/components/PayRatesEditor";
import { StatusPill } from "@/components/StaffDocuments";
import { P46RequestButton, PasswordRequestButton } from "@/components/ChangePassword";

const EMPTY = { name: "", email: "", password: "", phone: "", role: "carer", hourly_rate: 14, mileage_rate: 0.45, contracted_hours: 37.5, is_driver: true };

const CONCERN = new Set(["missing", "expiring", "expired", "lapsed", "pending", "not_registered", "not_requested"]);
function carerNeedsAttention(s) {
  const rtw = s.right_to_work || {};
  const c = s.compliance || {};
  const checks = [rtw.status, c.pvg?.status, c.employment_reference?.status, c.character_reference?.status];
  if (c.nmc?.applicable) checks.push(c.nmc?.status);
  if (c.visa?.required) checks.push(c.visa?.status);
  return checks.some((x) => CONCERN.has(x));
}

const DEFAULT_COMPLIANCE = {
  pvg: { status: "missing", number: "", issue_date: "", expiry_date: "" },
  sssc: { status: "not_registered", number: "", part: "", expiry_date: "" },
  nmc: { applicable: false, status: "n/a", number: "", expiry_date: "" },
  visa: { required: false, type: "", status: "n/a", expiry_date: "" },
  employment_reference: { status: "pending", notes: "" },
  character_reference: { status: "pending", notes: "" },
};
const DEFAULT_RTW = { status: "missing", document_type: "", document_number: "", expiry_date: "", notes: "" };

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [compFor, setCompFor] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const [accessFor, setAccessFor] = useState(null);
  const [availFor, setAvailFor] = useState(null);
  const [hrFor, setHrFor] = useState(null);
  const [contractsFor, setContractsFor] = useState(null);
  const [famFor, setFamFor] = useState(null);
  const [areasFor, setAreasFor] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [tab, setTab] = useState("people");
  const [clients, setClients] = useState([]);
  const { isAdmin, user } = useAuth();
  const canManage = isAdmin || user?.role === "office";

  const load = () => api.get("/staff").then((r) => setStaff(r.data));
  const editEmail = async (s) => {
    const email = window.prompt(`Real email address for ${s.name}:`, s.email);
    if (!email || email.trim().toLowerCase() === s.email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Please enter a valid email address, e.g. name@example.com"); return; }
    try { await api.put(`/staff/${s.id}`, { email: email.trim() }); toast.success(`Email updated for ${s.name}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/staff", form); toast.success("Befriender login created"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try { await api.delete(`/staff/${s.id}`); toast.success("Removed"); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Staff &amp; befrienders</h1>
          <p className="mt-1 text-ink-soft">Befrienders, office coordinators and admins — logins, hours, pay and compliance.</p>
          <HeaderAccent />
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <P46RequestButton />}
          {isAdmin && <PasswordRequestButton />}
          {user?.role !== "viewer" && (
            <button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-staff-button"
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
              <Plus size={18} /> Create login
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2" data-testid="staff-tabs">
        {[["people", "People"], ["coverage", "Coverage"], ...(canManage ? [["agreements", "Agreements"]] : [])].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`staff-tab-${k}`}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-ink text-white" : "bg-secondary text-ink hover:bg-brand-light"}`}>{l}</button>
        ))}
      </div>

      {tab === "coverage" && <CoverageView staff={staff} clients={clients} />}
      {tab === "agreements" && canManage && <StaffAgreementsAdmin isAdmin={isAdmin} />}

      {tab === "people" && <div className="flex flex-wrap gap-2" data-testid="staff-role-filter">
        {[["all", "All"], ["carer", "Befrienders"], ["office", "Office"], ["admin", "Admin"]].map(([k, l]) => (
          <button key={k} onClick={() => setRoleFilter(k)} data-testid={`role-filter-${k}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${roleFilter === k ? "bg-brand text-white" : "border border-border bg-white text-ink-soft hover:bg-secondary"}`}>{l}</button>
        ))}
      </div>}

      {tab === "people" && (() => {
        const befrienders = staff.filter((s) => s.role === "carer");
        const others = staff.filter((s) => s.role !== "carer");
        const needs = befrienders.filter(carerNeedsAttention);
        const showBef = roleFilter === "all" || roleFilter === "carer";
        const showOthers = roleFilter === "all" || roleFilter === "office" || roleFilter === "admin";
        const othersFiltered = roleFilter === "office" ? others.filter((s) => s.role === "office")
          : roleFilter === "admin" ? others.filter((s) => s.role === "admin") : others;
        const renderStaffCard = (s) => {
          const comp = s.compliance || DEFAULT_COMPLIANCE;
          const rtw = s.right_to_work || DEFAULT_RTW;
          return (
            <div key={s.id} data-testid={`staff-card-${s.id}`}
              className="rounded-xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slateblue-light font-heading font-bold text-slateblue">
                    {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-ink">{s.name}</h3>
                    {(() => { const rm = {admin:["Owner / Admin","bg-gold-light text-gold-dark"],office:["Office staff","bg-slateblue-light text-slateblue"],carer:["Befriender","bg-brand-light text-brand"],viewer:["Read-only","bg-secondary text-ink-soft"]}[s.role] || [s.role,"bg-secondary text-ink-soft"]; return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${rm[1]}`}>{rm[0]}</span>; })()}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm text-ink-soft">
                <p className="flex items-center gap-2"><Mail size={15} className="text-ink-muted" /><span className="truncate" data-testid={`staff-email-${s.id}`}>{s.email}</span>
                  {canManage && <button onClick={() => editEmail(s)} data-testid={`edit-email-button-${s.id}`} title="Change email" className="rounded p-1 text-ink-muted hover:bg-secondary hover:text-brand"><Pencil size={13} /></button>}</p>
                {s.phone && <p className="flex items-center gap-2"><Phone size={15} className="text-ink-muted" />{s.phone}</p>}
                {s.role === "carer" && <p className="flex items-center gap-2"><PoundSterling size={15} className="text-ink-muted" />£{s.hourly_rate}/hr{(s.is_driver ?? true) ? ` · £${s.mileage_rate}/mile` : ""}</p>}
                {s.role === "carer" && <p className="flex items-center gap-2"><Car size={15} className="text-ink-muted" />{(s.is_driver ?? true) ? "Driver — can claim mileage" : "Non-driver"}</p>}
                {s.role === "carer" && <p className="flex items-center gap-2"><Clock size={15} className="text-ink-muted" />{s.contracted_hours}h contracted / week</p>}
              </div>
              {s.role === "carer" && (
                <>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(rtw.status)}`}>RTW: {rtw.status}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(comp.pvg?.status)}`}>PVG: {comp.pvg?.status}</span>
                    {comp.pvg?.applied_date && comp.pvg?.status !== "valid" && <span data-testid={`pvg-applied-badge-${s.id}`} className="rounded-full bg-slateblue-light px-2 py-0.5 text-[11px] font-semibold text-slateblue">PVG applied {ukDate(comp.pvg.applied_date)}</span>}
                    <span data-testid={`sssc-badge-${s.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${comp.sssc?.status === "registered" ? tone("registered") : "bg-secondary text-ink-muted"}`}>SSSC (optional): {comp.sssc?.status?.replace("_", " ") || "not registered"}</span>
                    {comp.nmc?.applicable && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(comp.nmc?.status)}`}>NMC: {comp.nmc?.status}</span>}
                    {comp.visa?.required && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(comp.visa?.status)}`}>Visa: {comp.visa?.status}</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(comp.employment_reference?.status)}`}>Emp ref: {comp.employment_reference?.status?.replace("_", " ")}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone(comp.character_reference?.status)}`}>Char ref: {comp.character_reference?.status?.replace("_", " ")}</span>
                  </div>
                  <div className="mt-3 space-y-1 text-xs" data-testid={`card-work-prefs-${s.id}`}>
                    <p className="flex items-start gap-1.5 text-ink-soft"><MapPin size={13} className="mt-0.5 shrink-0 text-brand" />
                      {(s.work_areas || []).length ? <span>{s.work_areas.slice(0, 4).join(", ")}{s.work_areas.length > 4 ? ` +${s.work_areas.length - 4} more` : ""}</span> : <span className="italic text-ink-muted">No work areas chosen yet</span>}</p>
                    <p className="flex items-start gap-1.5 text-ink-soft"><HeartHandshake size={13} className="mt-0.5 shrink-0 text-brand" />
                      {(s.offered_services || []).length ? <span>{s.offered_services.length} service{s.offered_services.length === 1 ? "" : "s"} offered</span> : <span className="italic text-ink-muted">No services chosen yet</span>}</p>
                  </div>
                  {canManage && (
                    <button onClick={() => setAreasFor(s)} data-testid={`areas-button-${s.id}`}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-brand/30 bg-brand-light/40 py-2 text-sm font-medium text-brand transition-colors hover:bg-brand-light">
                      <MapPin size={14} /> Areas &amp; services
                    </button>
                  )}
                  <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
                    <button onClick={() => setCompFor(s)} data-testid={`rtw-button-${s.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-sm font-medium text-ink transition-colors hover:bg-secondary">
                      <FileCheck size={14} /> Compliance
                    </button>
                    <button onClick={() => setAvailFor(s)} data-testid={`availability-button-${s.id}`} title="Availability"
                      className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-ink transition-colors hover:bg-secondary">
                      <Clock size={14} />
                    </button>
                    {canManage && s.role === "carer" && (
                      <button onClick={() => setFamFor(s)} data-testid={`family-profile-button-${s.id}`} title="Family profile"
                        className="relative flex items-center justify-center rounded-lg border border-border px-3 py-2 text-ink transition-colors hover:bg-secondary">
                        <HeartHandshake size={14} />
                        {(s.gallery || []).some((g) => g.status === "pending") && <span data-testid={`gallery-pending-dot-${s.id}`} title="Photos awaiting approval" className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-coral" />}
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => setHrFor(s)} data-testid={`hr-button-${s.id}`} title="HR record"
                        className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-ink transition-colors hover:bg-secondary">
                        <Briefcase size={14} />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => setAccessFor(s)} data-testid={`access-button-${s.id}`} title="Access & role"
                        className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-ink transition-colors hover:bg-secondary">
                        <SlidersHorizontal size={14} />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => setPwFor(s)} data-testid={`reset-pw-${s.id}`} title="Reset password"
                        className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-ink transition-colors hover:bg-secondary">
                        <KeyRound size={14} />
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => setContractsFor(s)} data-testid={`contracts-button-${s.id}`} title="Contracts"
                        className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-ink transition-colors hover:bg-secondary">
                        <FileSignature size={14} />
                      </button>
                    )}
                    <button onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`}
                      className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-coral transition-colors hover:bg-coral-light">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
              {s.role !== "carer" && isAdmin && (
                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
                  <button onClick={() => setAccessFor(s)} data-testid={`access-button-${s.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary">
                    <SlidersHorizontal size={14} /> Access &amp; role
                  </button>
                  <button onClick={() => setHrFor(s)} data-testid={`hr-button-${s.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary">
                    <Briefcase size={14} /> HR record
                  </button>
                  {s.role === "office" && (
                    <button onClick={() => setContractsFor(s)} data-testid={`contracts-button-${s.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary">
                      <FileSignature size={14} /> Contracts
                    </button>
                  )}
                  {s.role !== "admin" && (
                    <button onClick={() => setCompFor(s)} data-testid={`rtw-button-${s.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary">
                      <FileCheck size={14} /> Compliance
                    </button>
                  )}
                  <button onClick={() => setPwFor(s)} data-testid={`reset-pw-${s.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink hover:bg-secondary">
                    <KeyRound size={14} /> Reset password
                  </button>
                  {s.role !== "admin" && (
                    <button onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`}
                      className="flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-coral hover:bg-coral-light">
                      <Trash2 size={14} /> Remove login
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        };
        return (
          <div className="space-y-8">
            <div data-testid="compliance-snapshot" className={`flex items-center gap-3 rounded-xl border p-4 ${needs.length ? "border-gold/40 bg-gold-light" : "border-sage/40 bg-sage-light"}`}>
              <ShieldCheck size={18} className={needs.length ? "text-gold-dark" : "text-sage-dark"} />
              <span className={`text-sm font-medium ${needs.length ? "text-gold-dark" : "text-sage-dark"}`}>
                {needs.length === 0
                  ? `All ${befrienders.length} befriender${befrienders.length === 1 ? "" : "s"} have compliance in order.`
                  : `${needs.length} of ${befrienders.length} befriender${befrienders.length === 1 ? "" : "s"} have expiring or missing compliance documents.`}
              </span>
            </div>
            {showBef && (
              <section data-testid="befrienders-section">
                <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Befrienders <span className="ml-1 text-sm font-normal text-ink-muted">({befrienders.length})</span></h2>
                {befrienders.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-border bg-white py-8 text-center text-sm text-ink-muted">No befrienders yet.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">{befrienders.map(renderStaffCard)}</div>
                )}
              </section>
            )}
            {showOthers && (
              <section data-testid="office-staff-section">
                <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Office &amp; admin staff <span className="ml-1 text-sm font-normal text-ink-muted">({othersFiltered.length})</span></h2>
                {othersFiltered.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-border bg-white py-8 text-center text-sm text-ink-muted">No office or admin staff yet.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">{othersFiltered.map(renderStaffCard)}</div>
                )}
              </section>
            )}
          </div>
        );
      })()}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <form onSubmit={create} data-testid="staff-form" className="relative z-10 max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ink">Create staff login</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-ink">Full name *</label>
                <input required data-testid="staff-name-input" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Email *</label>
                <input required type="email" data-testid="staff-email-input" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Temporary password *</label>
                <input required data-testid="staff-password-input" className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Phone</label>
                <input className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Role *</label>
                <select data-testid="staff-role-select" className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="carer">Befriender</option>
                  <option value="office">Office staff (coordinator)</option>
                  <option value="viewer">Read-only (auditor / trustee)</option>
                </select>
                {form.role === "office" && <p className="mt-1 text-xs text-ink-muted">Office staff can manage clients, rota, incidents, family access and logins — but not payroll or billing, and cannot delete befrienders.</p>}
              </div>
              {form.role === "carer" && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="mb-1 block text-sm font-medium text-ink">£/hr</label>
                      <input type="number" step="0.25" className={input} value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) })} /></div>
                    <div><label className="mb-1 block text-sm font-medium text-ink">£/mile</label>
                      <input type="number" step="0.05" className={input} value={form.mileage_rate} onChange={(e) => setForm({ ...form, mileage_rate: parseFloat(e.target.value) })} /></div>
                    <div><label className="mb-1 block text-sm font-medium text-ink">Hrs/wk</label>
                      <input type="number" step="0.5" data-testid="staff-contracted-input" className={input} value={form.contracted_hours} onChange={(e) => setForm({ ...form, contracted_hours: parseFloat(e.target.value) })} /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-medium text-ink">
                    <input type="checkbox" data-testid="staff-driver-checkbox" className="h-4 w-4 accent-brand" checked={form.is_driver} onChange={(e) => setForm({ ...form, is_driver: e.target.checked })} />
                    Driver (can claim mileage)
                  </label>
                </>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button type="submit" data-testid="save-staff-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Create login</button>
            </div>
          </form>
        </div>
      )}

      {compFor && <ComplianceModal staff={compFor} onClose={() => setCompFor(null)} onSaved={() => { setCompFor(null); load(); }} />}
      {pwFor && <ResetPasswordModal staff={pwFor} onClose={() => setPwFor(null)} onSaved={() => setPwFor(null)} />}
      {accessFor && <AccessModal staff={accessFor} onClose={() => setAccessFor(null)} onSaved={() => { setAccessFor(null); load(); }} />}
      {famFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFamFor(null)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="family-profile-modal">
            <div className="mb-1 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">{famFor.name} · Family profile</h2><button onClick={() => setFamFor(null)}><X size={20} className="text-ink-muted" /></button></div>
            <p className="mb-4 text-sm text-ink-soft">Shown to families on their portal so they can get to know their befriender.</p>
            <FamilyProfileEditor staff={famFor} onSaved={() => { load(); setFamFor(null); }} />
            <div className="mt-5 border-t border-border pt-4">
              <h3 className="mb-1 font-heading text-base font-semibold text-ink">Activity photos</h3>
              <p className="mb-3 text-sm text-ink-soft">Approve photos before families can see them, or remove any photo.</p>
              <ActivityGallery staffId={famFor.id} manage />
            </div>
          </div>
        </div>
      )}

      {areasFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAreasFor(null)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="areas-modal">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">{areasFor.name} · Areas &amp; services</h2><button onClick={() => setAreasFor(null)}><X size={20} className="text-ink-muted" /></button></div>
            <StaffWorkPrefs staff={areasFor} onSaved={() => { load(); setAreasFor(null); }} />
          </div>
        </div>
      )}

      {availFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAvailFor(null)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="availability-modal">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">{availFor.name} · Availability</h2><button onClick={() => setAvailFor(null)}><X size={20} className="text-ink-muted" /></button></div>
            <AvailabilityEditor kind="staff" id={availFor.id} initial={availFor.availability} initialBlocked={availFor.blocked_dates} onSaved={() => { load(); }} />
            {availFor.role === "carer" && (
              <div className="mt-5 border-t border-border pt-4">
                <StaffWorkPrefs staff={availFor} onSaved={load} />
              </div>
            )}
            {availFor.role === "carer" && (
              <div className="mt-5 border-t border-border pt-4">
                <StaffExclusions staff={availFor} clients={clients} onChange={() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }} />
              </div>
            )}
          </div>
        </div>
      )}
      {hrFor && <HRModal staff={hrFor} onClose={() => setHrFor(null)} onSaved={() => { setHrFor(null); load(); }} />}
      {contractsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setContractsFor(null)} />
          <div className="relative z-10 max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="staff-contracts-modal">
            <div className="mb-1 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">Contracts — {contractsFor.name}</h2><button onClick={() => setContractsFor(null)} data-testid="staff-contracts-close"><X size={20} className="text-ink-muted" /></button></div>
            <p className="mb-4 text-sm text-ink-soft">Every freelance agreement and contract of employment sent to {contractsFor.name.split(" ")[0]}. Send new ones from the Agreements tab.</p>
            <ContractsList url={`/staff/${contractsFor.id}/agreements`} empty="Nothing sent yet." />
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyProfileEditor({ staff, onSaved }) {
  const [f, setF] = useState({ bio: staff.bio || "", interests: staff.interests || "", activities: staff.activities || "", match_note: staff.match_note || "" });
  const [busy, setBusy] = useState(false);
  const inp = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const save = async () => {
    setBusy(true);
    try { await api.put(`/staff/${staff.id}/family-profile`, f); toast.success("Family profile saved"); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="family-profile-editor">
      <div><label className="mb-1 block text-sm font-medium text-ink">Why they're a great match</label>
        <textarea rows={2} className={inp} value={f.match_note} onChange={(e) => setF({ ...f, match_note: e.target.value })} data-testid="fam-match-note" placeholder="e.g. Shares Margaret's love of gardening and classic films — warm and patient." /></div>
      <div><label className="mb-1 block text-sm font-medium text-ink">Short introduction (bio)</label>
        <textarea rows={3} className={inp} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="fam-bio" /></div>
      <div><label className="mb-1 block text-sm font-medium text-ink">Interests</label>
        <textarea rows={2} className={inp} value={f.interests} onChange={(e) => setF({ ...f, interests: e.target.value })} data-testid="fam-interests" /></div>
      <div><label className="mb-1 block text-sm font-medium text-ink">Activities they enjoy on visits</label>
        <textarea rows={2} className={inp} value={f.activities} onChange={(e) => setF({ ...f, activities: e.target.value })} data-testid="fam-activities" /></div>
      <button onClick={save} disabled={busy} data-testid="save-family-profile" className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Saving…" : "Save family profile"}</button>
    </div>
  );
}

function StaffWorkPrefs({ staff, onSaved }) {
  const [v, setV] = useState({ work_areas: staff.work_areas || [], offered_services: staff.offered_services || [] });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/staff/${staff.id}/work-prefs`, v); toast.success("Areas & services saved"); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="staff-work-prefs">
      <h3 className="mb-3 font-heading text-base font-semibold text-ink">Work areas &amp; services</h3>
      <WorkPrefsEditor value={v} onChange={setV} />
      <button onClick={save} disabled={busy} data-testid="save-staff-work-prefs" className="mt-3 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Saving…" : "Save areas & services"}</button>
    </div>
  );
}

function StaffExclusions({ staff, clients, onChange }) {
  const initial = clients.filter((c) => (c.excluded_carers || []).includes(staff.id)).map((c) => c.id);
  const [sel, setSel] = useState(initial);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/staff/${staff.id}/exclusions`, { client_ids: sel }); toast.success("Do-not-pair list saved"); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="staff-exclusions-section">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Ban size={13} /> Do not pair with</p>
      <p className="mt-1 text-xs text-ink-muted">Tick any client this befriender should not be sent to (either party asked not to be paired). Blocks scheduling &amp; check-in.</p>
      <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
        {clients.length === 0 && <p className="text-sm text-ink-muted">No clients found.</p>}
        {clients.map((c) => (
          <label key={c.id} data-testid={`exclude-client-${c.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-secondary">
            <input type="checkbox" className="h-4 w-4 accent-coral" checked={sel.includes(c.id)} onChange={() => toggle(c.id)} />
            <span className={sel.includes(c.id) ? "font-medium text-coral-dark" : "text-ink"}>{c.name}</span>
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="save-staff-exclusions-button"
        className="mt-2 rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{saving ? "Saving…" : "Save do-not-pair list"}</button>
    </div>
  );
}


const HR_FIELDS = [
  ["job_title", "Job title"], ["employment_start", "Employment start date", "date"],
  ["contract_type", "Contract type (full/part-time, bank, volunteer)"], ["weekly_hours", "Contracted weekly hours"],
  ["salary_rate", "Salary / hourly rate"], ["ni_number", "National Insurance no."],
  ["holiday_entitlement", "Holiday entitlement (days)"], ["bank_account", "Bank details (payroll)"],
  ["next_of_kin_name", "Next of kin"], ["next_of_kin_phone", "Next of kin phone"],
  ["last_appraisal", "Last appraisal", "date"], ["next_appraisal", "Next appraisal due", "date"],
];
const HR_DOCS = ["Signed contract of employment", "Job description", "DBS / PVG certificate", "Right to work evidence", "Two references", "Proof of ID / passport", "Proof of address", "Induction record", "Confidentiality agreement", "Health questionnaire", "Training certificates", "Supervision & appraisal notes"];
const DOC_STATUS = ["Pending", "Received", "Expired", "N/A"];

function HRModal({ staff, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    const init = { ...(staff.hr || {}) };
    if (!init.documents || init.documents.length === 0) init.documents = HR_DOCS.map((n) => ({ name: n, status: "Pending", note: "" }));
    return init;
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setDoc = (i, k, v) => setF((x) => ({ ...x, documents: x.documents.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/staff/${staff.id}/hr`, { ...f }); toast.success("HR record saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const inp = "w-full rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="hr-modal" className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl scrollbar-thin">
        <div className="mb-1 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">{staff.name} · HR record</h2><button onClick={onClose}><X size={20} className="text-ink-muted" /></button></div>
        <p className="mb-4 text-sm text-ink-soft">Confidential employment record. Fill what you have — you can edit any time.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {HR_FIELDS.map(([k, label, type]) => (
            <div key={k}><label className="mb-1 block text-xs font-medium text-ink-muted">{label}</label><input type={type || "text"} data-testid={`hr-${k}`} className={inp} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} /></div>
          ))}
        </div>
        <div className="mt-3"><label className="mb-1 block text-xs font-medium text-ink-muted">Notes</label><textarea rows={2} className={inp} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        <p className="mt-5 mb-2 text-sm font-semibold text-ink">HR documents checklist</p>
        <div className="space-y-1.5">
          {f.documents.map((d, i) => (
            <div key={i} data-testid={`hr-doc-${i}`} className="grid grid-cols-12 items-center gap-2">
              <span className="col-span-12 text-sm text-ink sm:col-span-5">{d.name}</span>
              <select className={`${inp} col-span-5 sm:col-span-3`} value={d.status} onChange={(e) => setDoc(i, "status", e.target.value)}>{DOC_STATUS.map((s) => <option key={s}>{s}</option>)}</select>
              <input className={`${inp} col-span-7 sm:col-span-4`} placeholder="Ref / expiry / note" value={d.note || ""} onChange={(e) => setDoc(i, "note", e.target.value)} />
            </div>
          ))}
        </div>
        <div className="mt-6 flex gap-3"><button onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button><button onClick={save} disabled={saving} data-testid="save-hr-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{saving ? "Saving…" : "Save HR record"}</button></div>
      </div>
    </div>
  );
}

const ROLE_OPTIONS = [
  ["carer", "Befriender"],
  ["office", "Office coordinator"],
  ["viewer", "Read-only (auditor)"],
  ["admin", "Owner / Admin"],
];

function AccessModal({ staff, onClose, onSaved }) {
  const [role, setRole] = useState(staff.role);
  const [pages, setPages] = useState(staff.permissions || []);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setPages((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/staff/${staff.id}/access`, { role, permissions: pages });
      toast.success(`Access updated for ${staff.name}`); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="access-modal" className="relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl scrollbar-thin">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">Access &amp; permissions</h2>
          <button onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <p className="mb-5 text-sm text-ink-soft">Set the role and exactly which sections <span className="font-semibold">{staff.name}</span> can open. Admins always have full access.</p>

        <label className="mb-1 block text-sm font-medium text-ink">Role</label>
        <select data-testid="access-role-select" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        <div className={`mt-5 ${role === "admin" ? "opacity-50" : ""}`}>
          <p className="mb-2 text-sm font-medium text-ink">Pages this person can access</p>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {PAGES.map(([key, label]) => (
              <label key={key} data-testid={`access-page-${key}`}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${(role === "admin" || pages.includes(key)) ? "border-brand/40 bg-brand-light/40 text-ink" : "border-border text-ink-soft hover:bg-secondary"}`}>
                <input type="checkbox" className="h-4 w-4 accent-brand" disabled={role === "admin" || key === "dashboard"}
                  checked={role === "admin" || pages.includes(key)} onChange={() => toggle(key)} data-testid={`access-page-checkbox-${key}`} />
                {label}
              </label>
            ))}
          </div>
          {role === "admin" && <p className="mt-2 text-xs text-ink-muted">Admins have access to every section by default.</p>}
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-access-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Saving…" : "Save access"}</button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ staff, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const save = async () => {
    if (pw.trim().length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try { await api.put(`/staff/${staff.id}/password`, { new_password: pw.trim() }); toast.success(`Password reset for ${staff.name}`); onSaved(); }
    catch (e) { toast.error("Could not reset password"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="reset-password-modal" className="relative z-10 w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">Reset password</h2>
          <button onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <p className="mb-4 text-sm text-ink-soft">Set a new password for <span className="font-semibold">{staff.name}</span> ({staff.email}). Share it with them securely.</p>
        <input type="text" data-testid="new-password-input" className={input} placeholder="New password (min 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} />
        <div className="mt-5 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-password-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Saving…" : "Save password"}</button>
        </div>
      </div>
    </div>
  );
}

function ComplianceModal({ staff, onClose, onSaved }) {
  const [rtw, setRtw] = useState({ ...DEFAULT_RTW, ...(staff.right_to_work || {}) });
  const [comp, setComp] = useState({
    pvg: { ...DEFAULT_COMPLIANCE.pvg, ...(staff.compliance?.pvg || {}) },
    sssc: { ...DEFAULT_COMPLIANCE.sssc, ...(staff.compliance?.sssc || {}) },
    nmc: { ...DEFAULT_COMPLIANCE.nmc, ...(staff.compliance?.nmc || {}) },
    visa: { ...DEFAULT_COMPLIANCE.visa, ...(staff.compliance?.visa || {}) },
    employment_reference: { ...DEFAULT_COMPLIANCE.employment_reference, ...(staff.compliance?.employment_reference || {}) },
    character_reference: { ...DEFAULT_COMPLIANCE.character_reference, ...(staff.compliance?.character_reference || {}) },
  });
  const [hours, setHours] = useState(staff.contracted_hours ?? 37.5);
  const [isDriver, setIsDriver] = useState(staff.is_driver ?? true);
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const set = (grp, k, v) => setComp((c) => ({ ...c, [grp]: { ...c[grp], [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/staff/${staff.id}`, { right_to_work: rtw, compliance: comp, contracted_hours: parseFloat(hours), is_driver: isDriver });
      toast.success("Compliance updated"); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };

  const Sel = ({ value, onChange, options, testid }) => (
    <select data-testid={testid} className={input} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
    </select>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={save} data-testid="compliance-form" className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl scrollbar-thin">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">Compliance — {staff.name}</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <p className="mb-5 text-sm text-ink-soft">Right to work, PVG, SSSC, NMC, visa & references.</p>

        <Section title="Contracted hours">
          <div className="max-w-[180px]">
            <label className="mb-1 block text-xs font-medium text-ink-soft">Hours per week</label>
            <input type="number" step="0.5" data-testid="compliance-hours-input" className={input} value={hours} onChange={(e) => setHours(e.target.value)} />
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm font-medium text-ink">
            <input type="checkbox" data-testid="compliance-driver-checkbox" className="h-4 w-4 accent-brand" checked={isDriver} onChange={(e) => setIsDriver(e.target.checked)} />
            Driver (can claim mileage)
          </label>
        </Section>

        {staff.role === "carer" && (
          <Section title="Pay rates">
            <PayRatesEditor staffId={staff.id} />
          </Section>
        )}

        <Section title="Right to work">
          <Grid>
            <F label="Status"><Sel testid="rtw-status-select" value={rtw.status} onChange={(v) => setRtw({ ...rtw, status: v })}
              options={[["valid", "Verified / valid"], ["expiring", "Expiring soon"], ["missing", "Missing"]]} /></F>
            <F label="Document type"><input className={input} value={rtw.document_type} onChange={(e) => setRtw({ ...rtw, document_type: e.target.value })} /></F>
            <F label="Document number"><input className={input} value={rtw.document_number} onChange={(e) => setRtw({ ...rtw, document_number: e.target.value })} /></F>
            <F label="Expiry date"><UkDateInput value={rtw.expiry_date} onChange={(v) => setRtw({ ...rtw, expiry_date: v })} testid="rtw-expiry-input" /></F>
          </Grid>
        </Section>

        <Section title="PVG scheme (Protecting Vulnerable Groups)">
          <Grid>
            <F label="Status"><Sel testid="pvg-status-select" value={comp.pvg.status} onChange={(v) => set("pvg", "status", v)}
              options={[["valid", "Valid"], ["expiring", "Expiring"], ["pending", "Pending"], ["missing", "Missing"]]} /></F>
            <F label="PVG number"><input className={input} value={comp.pvg.number} onChange={(e) => set("pvg", "number", e.target.value)} /></F>
            <F label="Issue date"><UkDateInput value={comp.pvg.issue_date} onChange={(v) => set("pvg", "issue_date", v)} testid="pvg-issue-input" /></F>
            <F label="Expiry date"><UkDateInput value={comp.pvg.expiry_date} onChange={(v) => set("pvg", "expiry_date", v)} testid="pvg-expiry-input" /></F>
          </Grid>
        </Section>

        <Section title="SSSC registration (optional) — Scottish Social Services Council">
          <Grid>
            <F label="Status"><Sel value={comp.sssc.status} onChange={(v) => set("sssc", "status", v)}
              options={[["registered", "Registered"], ["pending", "Pending"], ["not_registered", "Not registered"]]} /></F>
            <F label="Registration number"><input className={input} value={comp.sssc.number} onChange={(e) => set("sssc", "number", e.target.value)} /></F>
            <F label="Part of register"><input className={input} value={comp.sssc.part} onChange={(e) => set("sssc", "part", e.target.value)} /></F>
            <F label="Renewal date"><UkDateInput value={comp.sssc.expiry_date} onChange={(v) => set("sssc", "expiry_date", v)} testid="sssc-expiry-input" /></F>
          </Grid>
        </Section>

        <Section title="NMC registration (if applicable)">
          <label className="mb-3 flex items-center gap-2 text-sm font-medium text-ink">
            <input type="checkbox" className="h-4 w-4 accent-brand" checked={comp.nmc.applicable} onChange={(e) => set("nmc", "applicable", e.target.checked)} />
            This worker is a registered nurse/midwife
          </label>
          {comp.nmc.applicable && (
            <Grid>
              <F label="Status"><Sel value={comp.nmc.status} onChange={(v) => set("nmc", "status", v)}
                options={[["valid", "Valid"], ["expiring", "Expiring"], ["lapsed", "Lapsed"]]} /></F>
              <F label="NMC PIN"><input className={input} value={comp.nmc.number} onChange={(e) => set("nmc", "number", e.target.value)} /></F>
              <F label="Revalidation date"><UkDateInput value={comp.nmc.expiry_date} onChange={(v) => set("nmc", "expiry_date", v)} testid="nmc-expiry-input" /></F>
            </Grid>
          )}
        </Section>

        <Section title="Visa / immigration">
          <label className="mb-3 flex items-center gap-2 text-sm font-medium text-ink">
            <input type="checkbox" data-testid="visa-required-checkbox" className="h-4 w-4 accent-brand" checked={comp.visa.required} onChange={(e) => set("visa", "required", e.target.checked)} />
            Requires a visa to work in the UK
          </label>
          {comp.visa.required && (
            <Grid>
              <F label="Visa type"><input className={input} placeholder="e.g. Skilled Worker" value={comp.visa.type} onChange={(e) => set("visa", "type", e.target.value)} /></F>
              <F label="Status"><Sel value={comp.visa.status} onChange={(v) => set("visa", "status", v)}
                options={[["valid", "Valid"], ["expiring", "Expiring"], ["expired", "Expired"]]} /></F>
              <F label="Expiry date"><UkDateInput value={comp.visa.expiry_date} onChange={(v) => set("visa", "expiry_date", v)} testid="visa-expiry-input" /></F>
            </Grid>
          )}
        </Section>

        <Section title="References">
          <Grid>
            <F label="Employment reference"><Sel value={comp.employment_reference.status} onChange={(v) => set("employment_reference", "status", v)}
              options={[["received", "Received"], ["pending", "Pending"], ["not_requested", "Not requested"]]} /></F>
            <F label="Character reference"><Sel value={comp.character_reference.status} onChange={(v) => set("character_reference", "status", v)}
              options={[["received", "Received"], ["pending", "Pending"], ["not_requested", "Not requested"]]} /></F>
            <F label="Employment ref notes"><input className={input} value={comp.employment_reference.notes} onChange={(e) => set("employment_reference", "notes", e.target.value)} /></F>
            <F label="Character ref notes"><input className={input} value={comp.character_reference.notes} onChange={(e) => set("character_reference", "notes", e.target.value)} /></F>
          </Grid>
        </Section>

        <DocumentsSection staffId={staff.id} />

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button type="submit" data-testid="save-rtw-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Save compliance</button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-5 rounded-lg border border-border bg-ivory/40 p-4">
    <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand"><ShieldCheck size={13} /> {title}</p>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
const F = ({ label, children }) => (
  <div><label className="mb-1 block text-xs font-medium text-ink-soft">{label}</label>{children}</div>
);

function DocumentsSection({ staffId }) {
  const [docs, setDocs] = useState([]);
  const [dtype, setDtype] = useState("PVG certificate");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("bsb_token");
  const load = () => api.get(`/staff/${staffId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staffId]);
  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", dtype); fd.append("expiry_date", expiry);
    try { await api.post(`/staff/${staffId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded"); setFile(null); setExpiry(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (d) => { if (!window.confirm("Delete this document?")) return; try { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const review = async (d, status) => {
    const note = status === "rejected" ? window.prompt("Reason (sent to the befriender):", "") : "";
    if (note === null) return;
    try { await api.put(`/documents/${d.id}/review`, { status, note }); toast.success(status === "approved" ? "Approved" : "Rejected"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const view = (d) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download?auth=${token}`, "_blank");
  const input = "rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const expired = (e) => e && new Date(e) < new Date();
  return (
    <Section title="Compliance documents (PVG, visa, references)">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <select className={input} value={dtype} onChange={(e) => setDtype(e.target.value)} data-testid="doc-type-select">
          {["PVG certificate", "Passport / ID", "Proof of address", "Share code", "Visa document", "SSSC certificate", "NMC certificate", "Employment reference", "Character reference", "Right to work", "Other"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <div><label className="block text-[11px] text-ink-muted">Expiry (optional)</label>
          <UkDateInput value={expiry} onChange={setExpiry} testid="doc-expiry-input" /></div>
        <label className="cursor-pointer rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink hover:bg-secondary">
          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="doc-file-input" onChange={(e) => setFile(e.target.files[0])} />
          {file ? file.name.slice(0, 16) : "Choose file"}
        </label>
        <button type="button" onClick={upload} disabled={busy} data-testid="upload-doc-button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">
          <Upload size={15} /> Upload
        </button>
      </div>
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 rounded-lg border border-border bg-white p-2.5">
            <FileText size={16} className="text-brand" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{d.doc_type} · <span className="text-ink-muted">{d.original_filename}</span></p>
              {d.expiry_date && <p className={`text-xs ${expired(d.expiry_date) ? "text-coral" : "text-ink-muted"}`}>Expires {ukDate(d.expiry_date)}{expired(d.expiry_date) ? " (expired)" : ""}</p>}
            </div>
            {d.review_status && <StatusPill status={d.review_status} />}
            {d.review_status === "pending" && <>
              <button type="button" onClick={() => review(d, "approved")} data-testid={`staff-approve-doc-${d.id}`} className="rounded-lg bg-sage px-2 py-1 text-xs font-semibold text-white">Approve</button>
              <button type="button" onClick={() => review(d, "rejected")} data-testid={`staff-reject-doc-${d.id}`} className="rounded-lg border border-border px-2 py-1 text-xs font-semibold text-coral-dark">Reject</button>
            </>}
            <button type="button" onClick={() => view(d)} data-testid={`view-doc-${d.id}`} className="rounded-lg border border-border px-2 py-1 text-ink hover:bg-secondary"><Download size={14} /></button>
            <button type="button" onClick={() => del(d)} data-testid={`delete-doc-${d.id}`} className="rounded-lg border border-border px-2 py-1 text-coral hover:bg-coral-light"><Trash2 size={14} /></button>
          </div>
        ))}
        {docs.length === 0 && <p className="text-sm text-ink-muted">No documents uploaded yet.</p>}
      </div>
    </Section>
  );
}
